import { useEffect, useState } from 'react';
import { api, type EstateFindingRow, type Severity, type Tenant } from '../api.ts';
import { message, plural } from '../format.ts';
import './estate.css';

/**
 * The estate roll-up — every control failing anywhere, worst first.
 *
 * The Clients screen answers "which client needs me first"; this answers the
 * other question an MSP asks across a portfolio: "which control is failing for
 * the most people". A control failing across fifteen tenants is usually one
 * misconfiguration repeated, or a default nobody changed — a single fix that
 * clears fifteen findings — and that shape is invisible one client at a time.
 *
 * Counts, never proportions (ADR-0007). "12 clients fail AC-003" is a number an
 * auditor can recount; "80% coverage" buries the ones we could not evidence,
 * which are the ones most worth reading.
 *
 * **Severity comes from the catalogue, and the ordering is the server's.** The
 * response arrives worst-severity first, then most-failing, then id; this screen
 * renders it in that order rather than re-sorting, so the console cannot disagree
 * with the API about which control matters most.
 */

const SEVERITY_TEXT: Record<string, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const isSeverity = (value: string): value is Severity =>
  value === 'critical' || value === 'high' || value === 'medium' || value === 'low';

interface EstateProps {
  readonly onOpen: (tenant: Tenant) => void;
}

export const Estate = ({ onOpen }: EstateProps) => {
  const [findings, setFindings] = useState<EstateFindingRow[]>([]);
  /** Full records, so a failing-tenant name can open that tenant the way Clients does. */
  const [tenantsById, setTenantsById] = useState<ReadonlyMap<string, Tenant>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    let live = true;
    // The two reads are independent: the findings decide the screen, and the
    // tenant list only enriches a failing name into a link. A failure of the
    // second must not deny the first, so it is caught and dropped to an empty map.
    Promise.all([
      api.estateFindings(),
      api.listTenants().catch(() => ({ tenants: [] as Tenant[] })),
    ])
      .then(([estate, tenantList]) => {
        if (!live) return;
        setFindings(estate.findings);
        setTenantsById(new Map(tenantList.tenants.map((tenant) => [tenant.id, tenant])));
        setError(null);
      })
      .catch((cause: unknown) => {
        if (live) setError(message(cause));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, []);

  const affected = new Set(findings.flatMap((row) => row.tenants.map((tenant) => tenant.tenantId))).size;

  const toggle = (controlId: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (!next.delete(controlId)) next.add(controlId);
      return next;
    });
  };

  return (
    <div className="estate-screen">
      <p className="lead">
        Every control failing somewhere in your live estate, the ones failing for the most clients
        first. One control failing across many tenants is usually one fix, not many.
      </p>

      {error !== null && <div className="error">{error}</div>}

      {!loading && findings.length > 0 && (
        <div className="stat-grid es-summary">
          <div className="stat">
            <span className="stat-figure">{findings.length}</span>
            <span className="stat-label">Controls failing somewhere</span>
            <span className="stat-hint">Across the live estate</span>
          </div>
          <div className="stat">
            <span className="stat-figure">{affected}</span>
            <span className="stat-label">Clients affected</span>
            <span className="stat-hint">With at least one failing control</span>
          </div>
        </div>
      )}

      {loading ? (
        <p className="note" aria-live="polite">
          Reading the latest assessment of every connected client…
        </p>
      ) : findings.length === 0 && error === null ? (
        <div className="empty es-empty">
          <span className="empty-title">Nothing is failing across the estate</span>
          <p className="empty-body">
            No control is failing for any connected client at its latest assessment. This screen fills
            in as assessments find controls not met — it counts failures, never a score, so an empty
            screen here means exactly what it says.
          </p>
        </div>
      ) : (
        <ul className="es-list">
          {findings.map((row) => (
            <ControlCard
              key={row.controlId}
              row={row}
              open={expanded.has(row.controlId)}
              tenantsById={tenantsById}
              onToggle={() => toggle(row.controlId)}
              onOpen={onOpen}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

interface ControlCardProps {
  readonly row: EstateFindingRow;
  readonly open: boolean;
  readonly tenantsById: ReadonlyMap<string, Tenant>;
  readonly onToggle: () => void;
  readonly onOpen: (tenant: Tenant) => void;
}

const ControlCard = ({ row, open, tenantsById, onToggle, onOpen }: ControlCardProps) => {
  const panelId = `es-tenants-${row.controlId}`;
  return (
    <li className="card es-card">
      <button
        type="button"
        className="es-card-head"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span className="es-card-main">
          <span className="es-control">
            <span className="es-title">{row.title}</span>
            <span className="mono es-id">{row.controlId}</span>
          </span>
          {isSeverity(row.severity) ? (
            <SeverityBadge severity={row.severity} />
          ) : (
            <span className="note">{SEVERITY_TEXT[row.severity] ?? row.severity}</span>
          )}
        </span>
        <span className="es-card-right">
          <span className="es-failing">
            <span className="num es-failing-count">{row.failingCount}</span>
            <span className="es-failing-label">{row.failingCount === 1 ? 'client' : 'clients'} failing</span>
          </span>
          <span className="es-chevron" aria-hidden="true">
            {open ? '▾' : '▸'}
          </span>
        </span>
      </button>

      {open && (
        <div className="es-tenants" id={panelId}>
          <ul className="es-tenant-list">
            {row.tenants.map((tenant) => {
              const record = tenantsById.get(tenant.tenantId);
              return (
                <li key={tenant.tenantId} className="es-tenant">
                  {record !== undefined ? (
                    <button
                      type="button"
                      className="es-tenant-open"
                      aria-label={`Open ${tenant.displayName}`}
                      onClick={() => onOpen(record)}
                    >
                      {tenant.displayName}
                    </button>
                  ) : (
                    <span className="es-tenant-name">{tenant.displayName}</span>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="note es-tenant-note">
            {plural(row.tenants.length, 'client')} {row.tenants.length === 1 ? 'is' : 'are'} not
            meeting this control at their latest assessment.
          </p>
        </div>
      )}
    </li>
  );
};

interface SeverityBadgeProps {
  severity: Severity;
}

/** Exactly four marks, the first N filled — the contract `.sev` is built on. */
const SeverityBadge = ({ severity }: SeverityBadgeProps) => (
  <span className={`sev sev-${severity}`}>
    <span className="sev-ticks" aria-hidden="true">
      <span />
      <span />
      <span />
      <span />
    </span>
    {SEVERITY_TEXT[severity]}
  </span>
);
